/**
 * Custom paper cursor follow. Hides the native cursor (via styles.css)
 * and updates the #custom-cursor img's transform on pointermove so the
 * drawn arrow tip lands exactly at the mouse position.
 *
 * The PNG has its arrow tip at 43.04% / 31.53% of the image's bounds
 * (measured from the source PNG's alpha channel). The CSS already
 * positions the img at top:0 left:0; we translate by pointer coords
 * and back by the tip-relative percentages.
 *
 * Three lag-mitigation choices live here:
 *
 *  1. pointermove (not mousemove). Both fire roughly once per
 *     animation frame, but mousemove is more aggressively throttled
 *     in Safari under load; pointermove is the unified, modern API
 *     and also covers pen/touch with no extra code.
 *
 *  2. rAF-batched transform writes. We capture every pointermove's
 *     coords into closure state and schedule a SINGLE
 *     requestAnimationFrame to write the transform. If multiple
 *     pointer events fire per frame, only the latest position is
 *     written — no redundant style mutations, no layout/paint thrash.
 *
 *  3. No mouseleave/mouseenter fade. An opacity transition opened a
 *     180 ms window during which an OS-cursor flash (caused by the
 *     browser's per-frame cursor:none update lagging behind the OS
 *     hardware cursor at >1000 Hz) was visible *next to* a half-faded
 *     paper cursor. The img is now always rendered, parked at its
 *     last known position when the user moves over browser chrome.
 */
const TIP_X_PCT = 43.04;
const TIP_Y_PCT = 31.53;

export function attachCustomCursor() {
  const el = document.getElementById('custom-cursor') as HTMLImageElement | null;
  if (!el) return;

  // Latest pointer coords. The pointermove listener writes here; the rAF
  // callback reads. Initial values keep the cursor off-screen until the
  // first real pointer event lands.
  let targetX = -9999;
  let targetY = -9999;
  // True between rAF callbacks meaning "no write pending; the next
  // pointermove should request one." Toggling this flag ensures we
  // schedule at most one rAF per frame even under a pointer-event
  // storm (high-DPI mice can fire dozens per frame in some browsers).
  let writePending = false;

  const writeFrame = () => {
    writePending = false;
    // translate3d forces the cursor onto its own GPU compositor layer
    // (paired with will-change:transform in CSS). Cursor moves never
    // trigger main-thread paints — critical when the stage has many
    // filtered/blended overlays.
    el.style.transform =
      `translate3d(${targetX}px, ${targetY}px, 0) translate(-${TIP_X_PCT}%, -${TIP_Y_PCT}%)`;
  };

  const onPointerMove = (e: PointerEvent) => {
    // The main event always carries the LATEST hardware sample. Any
    // older intermediate samples available via getCoalescedEvents() are
    // irrelevant for a non-trailing cursor — we only care where the
    // pointer is right now.
    targetX = e.clientX;
    targetY = e.clientY;
    if (!writePending) {
      writePending = true;
      requestAnimationFrame(writeFrame);
    }
  };

  // `passive: true` lets the browser dispatch the event without waiting
  // for our handler to potentially preventDefault — measurably reduces
  // input-to-paint latency under load.
  document.addEventListener('pointermove', onPointerMove, { passive: true });

  // Final safety net for the "stuck OS cursor on top of the paper
  // cursor until you leave the window" bug: if anything in the page
  // ever tries to start a native HTML5 drag (an <img>, an SDK-injected
  // media element, anything draggable by default), block it before the
  // browser hands cursor rendering to the OS for the drag operation.
  // The CSS in styles.css already declares `user-drag: none` globally;
  // this is defense in depth for elements that bypass CSS (rare but
  // possible with shadow-DOM widgets or runtime-injected media).
  document.addEventListener('dragstart', e => e.preventDefault());

  // Same idea for selectstart: prevents accidental text-selection drag
  // (which puts the browser into I-beam-cursor mode and is similarly
  // sticky until mouseup). The global `user-select: none` covers most
  // of this in CSS, but selectstart preventDefault catches the edge
  // case of a shadow-DOM or content-editable region opting back in.
  document.addEventListener('selectstart', e => {
    // Allow selection inside form fields and explicit content-editable.
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
    e.preventDefault();
  });

  startEnforcer();
}

// ──────────────────────────────────────────────────────────────────────────
// Active enforcer.
//
// Runs UNCONDITIONALLY every 500ms, regardless of what the HUD reports
// or whether the inferred state is "hidden" or "VISIBLE". The HUD is a
// passive observer; this is the active hammer. On every tick it:
//
//   1. Re-asserts `cursor: <invisible-png>, none` with !important
//      priority on <html>, <body>, and #frame via inline style. Inline
//      style with !important beats every author-CSS rule and every
//      non-!important inline style — the only thing that can defeat it
//      is another !important-on-inline, which nothing in this codebase
//      sets. Same 1×1 transparent PNG data URI as the CSS, so the OS
//      gets a real (invisible) image cursor in any context where bare
//      `none` is flaky (shadow-DOM, SDK iframes).
//   2. Clears any text-selection range that slipped past the
//      selectstart guard. An I-beam-locked browser cleared this way
//      drops back to `cursor: none` the same frame.
//   3. Synthesizes a `pointerup` on document if a phantom button-held
//      state is plausibly still active (resets some browsers' internal
//      drag-threshold tracking).
//
// `window.__cursorEnforcer` exposes counters to the HUD so corrections
// are visible in real time.
// ──────────────────────────────────────────────────────────────────────────
type EnforcerStats = { ticks: number; corrections: number; lastCorrection: string };
declare global {
  interface Window { __cursorEnforcer?: EnforcerStats }
}

const INVISIBLE_CURSOR =
  'url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAC0lEQVR4nGNgAAIAAAUAAXpeqz8AAAAASUVORK5CYII=") 0 0, none';

function startEnforcer() {
  const stats: EnforcerStats = { ticks: 0, corrections: 0, lastCorrection: '–' };
  window.__cursorEnforcer = stats;

  /** Stamp inline `cursor` with !important priority. setProperty is the
   *  only API that lets us pass the priority flag from JS; assigning
   *  to `el.style.cursor` always omits !important. */
  const stampCursor = (el: HTMLElement | null, label: string) => {
    if (!el) return;
    // getPropertyValue + getPropertyPriority lets us know if the
    // current inline value is already what we want with !important.
    // Skip the write in that case to avoid pointless style invalidations.
    const cur = el.style.getPropertyValue('cursor');
    const prio = el.style.getPropertyPriority('cursor');
    if (cur === INVISIBLE_CURSOR && prio === 'important') return;
    el.style.setProperty('cursor', INVISIBLE_CURSOR, 'important');
    stats.corrections++;
    stats.lastCorrection = `${label} cursor`;
  };

  const tick = () => {
    stats.ticks++;
    // No focus check — runs whether the window is focused or not. The
    // "set it every 500ms regardless" requirement.

    // 1. Inline cursor with !important on root containers and the main
    //    game frame.
    stampCursor(document.documentElement, 'html');
    stampCursor(document.body, 'body');
    stampCursor(document.getElementById('frame'), '#frame');

    // 2. Kill stray selections. Cheap to check; only mutate when there's
    //    actually a non-empty range.
    const sel = document.getSelection();
    if (sel && sel.rangeCount > 0 && sel.toString().length > 0) {
      sel.removeAllRanges();
      stats.corrections++;
      stats.lastCorrection = 'selection cleared';
    }

    // 3. Fire a synthetic pointerup at the last-known-document position
    //    (1, 1) is a safe sentinel. This nudges browsers that track an
    //    internal "button-held" flag separately from event dispatch and
    //    can otherwise carry a stale held-state across focus changes.
    //    Cheap: most ticks the dispatched event has no listeners that
    //    care. Wrapped in try/catch because some browsers throw on
    //    PointerEvent construction without a primary input device.
    try {
      const pe = new PointerEvent('pointerup', {
        bubbles: true,
        cancelable: true,
        clientX: 0,
        clientY: 0,
        pointerType: 'mouse',
      });
      document.dispatchEvent(pe);
    } catch {
      /* PointerEvent constructor unavailable — ignore */
    }
  };

  // 500ms cadence. Slow enough that the per-tick work is invisible in
  // perf profiles; fast enough that any momentary slip is corrected
  // within half a second.
  setInterval(tick, 500);

  // Also re-tick on focus regain (covers the "user alt-tabbed away
  // mid-drag, came back, things are stale" case).
  window.addEventListener('focus', tick);

  // And immediately on attach so the inline styles are in place before
  // the first paint.
  tick();
}
